import React, { useEffect, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { HDialog, HDialogContent } from 'BaseComponents';
import { useListOrganizationMasterRuntimeParameters, useSyncParameterToMaster } from 'hooks/questionAPIs';


export const SyncParameterDialog = ({
  open, row, onClose, onSuccess,
}) => {
  const [existingSyncDefinition, setExistingSyncDefinition] = useState(null);

  const {
    mutate: syncParameter, isLoading: isSyncingParameter,
  } = useSyncParameterToMaster();
  const {
    data: orgParameters, isLoading: isLoadingParameters,
  } = useListOrganizationMasterRuntimeParameters(open);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setExistingSyncDefinition(null);
    }
  }, [open]);

  // Check if master already exists (just for display, backend handles it)
  useEffect(() => {
    if (open && orgParameters) {
      const matchingMaster = orgParameters.find(
        (m) => m.name === row?.name && m.type === row?.type,
      );

      setExistingSyncDefinition(matchingMaster || null);
    }
  }, [open, orgParameters, row]);

  const handleSync = () => {
    // Since independent params are 1-per-row, we take the parameter ID from the first question.
    const parameterID = row?.questions?.[0]?.parameterID;
    if (!parameterID) return;

    syncParameter(
      {
        parameterID,
        // Send ID to link if exists, else empty to auto-create a new sync definition.
        masterID: existingSyncDefinition?.ID || '',
      },
      {
        onSuccess: () => {
          onSuccess?.();
          onClose();
        },
      },
    );
  };

  return (
    <HDialog
      open={open}
      onClose={onClose}
      title='Sync Runtime Parameter'
      onSubmit={handleSync}
      submitText='Sync'
      loading={isSyncingParameter}
      disabled={isLoadingParameters}
      maxWidth='sm'
      fullWidth
    >
      <HDialogContent>
        {isLoadingParameters ? (
          <Typography>Loading...</Typography>
        ) : (
          <>
            <Typography variant='body1' gutterBottom>
              Are you sure you want to sync the parameter
              {' '}
              <strong>{row?.name}</strong>
              {' '}
              in sync?
            </Typography>

            {existingSyncDefinition ? (
              <Alert severity='info' sx={{ mt: 2 }}>
                <Box pl={2}>
                  <Typography variant='subtitle1'>
                    This parameter will be kept in sync with an existing parameter.
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: '1rem' }}>
                    <li>
                      <Typography variant='body2'>
                        It is already used in
                        {' '}
                        <strong>{existingSyncDefinition.usageCount || 0}</strong>
                        {' '}
                        other question(s) in your organization.
                      </Typography>
                    </li>
                    <li>
                      <Typography variant='body2'>
                        Its settings (Display Name, Description, etc.)
                        {' '}
                        will update to match the existing synced parameter.
                      </Typography>
                    </li>
                  </ul>
                </Box>
              </Alert>
            ) : (
              <Alert severity='success' sx={{ mt: 2 }}>
                <Box pl={2}>
                  <Typography variant='subtitle1'>
                    A new synced parameter will be created.
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: '1rem' }}>
                    <li>
                      <Typography variant='body2'>
                        This parameter will be used as the definition for syncing.
                      </Typography>
                    </li>
                    <li>
                      <Typography variant='body2'>
                        You can sync other &quot;
                        {row?.name}
                        &quot; parameters to this one later.
                      </Typography>
                    </li>
                  </ul>
                </Box>
              </Alert>
            )}
            <Typography display='block' variant='caption' mt={2}>
              Once synced, any future changes to this parameter will
              {' '}
              automatically apply to all questions using it.
            </Typography>
          </>
        )}
      </HDialogContent>
    </HDialog>
  );
};
